import { ApproveStatus } from "../types/claim";
import {
    InvoiceStateWithPaymentInfo,
    InvoiceStatus,
    InvoiceStatusWithPaymentInfo,
} from "../types/invoice";
import { UserRole } from "../types/userCurrent";

import { compareDateDesc } from "./common";
import { hasPreAndPaymentAction } from "./payment";
import { isUserContractor, isUserFinanceDepartment, isUserPO } from "./role";

export const hasInvoiceAction = (
    roles: UserRole[],
    invoiceStatus: InvoiceStatus,
): boolean => {
    switch (invoiceStatus) {
        case InvoiceStatus.CONTRACTOR_SUBMITTED:
            return isUserPO(roles);
        case InvoiceStatus.PO_APPROVED:
            return isUserFinanceDepartment(roles);
        case InvoiceStatus.PO_REJECTED:
            return isUserContractor(roles);
        case InvoiceStatus.FINANCE_REJECTED:
            return isUserContractor(roles);
        default:
            return false;
    }
};

export const hasNestedInvoiceAction = (
    roles: UserRole[],
    nestedInvoiceStatus: InvoiceStatusWithPaymentInfo,
    currentUserEmail?: string,
): boolean => {
    const validPaymentApprovers =
        nestedInvoiceStatus.paymentInfo?.paymentOrderApprovers.filter(
            (it) => it.status !== ApproveStatus.INVALID,
        );

    const currentApprover = validPaymentApprovers?.find(
        (it) => it.details.email === currentUserEmail,
    );
    const currentApproverIndex = validPaymentApprovers?.findIndex(
        (it) => it.details.email === currentUserEmail,
    );
    const previousApproverIndex =
        currentApproverIndex === undefined
            ? undefined
            : currentApproverIndex - 1;

    const isPreviousPriorityApproverApproved = (() => {
        if (previousApproverIndex === undefined) {
            return;
        }

        const isPreviousApproverApprovedOrInvalid =
            nestedInvoiceStatus.paymentInfo?.paymentOrderApprovers[
                previousApproverIndex
            ]?.status === ApproveStatus.APPROVED ||
            nestedInvoiceStatus.paymentInfo?.paymentOrderApprovers[
                previousApproverIndex
            ]?.status === ApproveStatus.INVALID;

        return previousApproverIndex >= 0
            ? isPreviousApproverApprovedOrInvalid
            : null;
    })();

    return (
        hasInvoiceAction(roles, nestedInvoiceStatus.invoiceStatus) ||
        hasPreAndPaymentAction(
            roles,
            nestedInvoiceStatus.paymentInfo?.paymentOrderStatus,
            nestedInvoiceStatus.paymentInfo?.paymentStatus,
            nestedInvoiceStatus.invoiceStatus,
            currentApprover,
            isPreviousPriorityApproverApproved,
        )
    );
};

export const getNestedInvoiceStatus = (
    nestedInvoiceState: InvoiceStateWithPaymentInfo,
): InvoiceStatusWithPaymentInfo => ({
    invoiceStatus: nestedInvoiceState.invoiceState.status,
    paymentInfo: nestedInvoiceState.paymentInfo,
});

const compareNestedInvoiceActionFirst =
    (roles: UserRole[], currentUserEmail?: string) =>
    (
        a: InvoiceStatusWithPaymentInfo,
        b: InvoiceStatusWithPaymentInfo,
    ): number => {
        if (
            hasNestedInvoiceAction(roles, a, currentUserEmail) &&
            hasNestedInvoiceAction(roles, b, currentUserEmail)
        ) {
            return 0;
        } else if (hasNestedInvoiceAction(roles, a, currentUserEmail)) {
            return -1;
        } else if (hasNestedInvoiceAction(roles, b, currentUserEmail)) {
            return 1;
        } else {
            return 0;
        }
    };

export const compareInvoiceDateDescAndActionFirst =
    (roles: UserRole[], currentUserEmail?: string) =>
    (
        a: InvoiceStateWithPaymentInfo,
        b: InvoiceStateWithPaymentInfo,
    ): number => {
        const compareActionResult = compareNestedInvoiceActionFirst(
            roles,
            currentUserEmail,
        )(getNestedInvoiceStatus(a), getNestedInvoiceStatus(b));
        if (compareActionResult === 0) {
            return compareDateDesc(a.invoiceState, b.invoiceState);
        } else {
            return compareActionResult;
        }
    };
